import {Anchor} from '@zendeskgarden/react-buttons';
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, SortableCell, Table} from '@zendeskgarden/react-tables';
import React, {CSSProperties, useEffect, useState} from 'react';
import {
    AccountsTableViewModel,
    VersionType
} from "../../../../__generated__/generated_types";

import AccountTableViewShimmer from "../../../../assets/images/shimmers/account-table-view.svg";
import AccountNoteModal, {AccountNoteModalFetchInputs} from '../../../../components/note/AccountNoteModal';
import AccountNoteViewButton from '../../../../components/note/AccountNoteViewButton';
import {
    StarredAccountForAccountsTableView
} from "../../../../components/starred-account/StarredAccountForAccoutsTableView";
import {Property, propertyDisplayBudgetYear} from "../../../../contexts/properties/logic";
import {useConfig} from "../../../../hooks/useConfig";
import {calcVarianceNullSafe, varianceAmount, varianceValToString} from "../../../../utils/math";
import {VarianceType} from "../../../../VarianceType";
import styles from "./AccountsTableView.module.scss";


export interface IRowData {
    accountId: string;
    glNumber: string;
    glName: string;
    isIncome: boolean;
    reforecastVersionId: string;
    budgetVersionId: string;
    isStarred: boolean;
    reforecastValue: number;
    budgetValue: number;
    varPercentage: number;
    varAmount: number;
    reforecastNotes: string | null;
    budgetNotes: string | null;
    varPercentageClassName: string;
}

type Direction = 'asc' | 'desc' | undefined;

type SortableFields = keyof Pick<IRowData, "glNumber" | "glName" | "varPercentage" | "varAmount">;

const sortData = (tableData: IRowData[], fieldName: SortableFields | undefined, sortDirection: Direction) => {
    if (!fieldName || !sortDirection) {
        return tableData;
    }
    return tableData.sort((a, b) => {
        const aValue = a[fieldName];
        const bValue = b[fieldName];

        // Keep NaNs always at the end in asc/desc sorting else the api returned order is good
        if (typeof aValue === "number" && isNaN(aValue)) {
            return 1;
        }
        if (typeof bValue === "number" && isNaN(bValue)) {
            return -1;
        }

        if (aValue > bValue) {
            return sortDirection === 'asc' ? 1 : -1;
        } else if (aValue < bValue) {
            return sortDirection === 'asc' ? -1 : 1;
        }
        return 0;
    });
};

interface AccountsTableViewProps {
    headerText: string;
    property: Property | undefined;
    tableData: AccountsTableViewModel[] | undefined;
    onStarringUpdate?: () => void;
    onNoteUpdate?: () => void;
    style?: CSSProperties;
}

export const AccountsTableView: React.VFC<AccountsTableViewProps> =
    ({
         headerText,
         property,
         tableData,
         onStarringUpdate,
         onNoteUpdate,
         style
     }) => {
        const [data, setData] = useState<IRowData[] | undefined>(undefined);
        const [sortByField, setSortByField] = useState<(SortableFields) | undefined>(undefined);
        const [sortDirection, setSortDirection] = useState<Direction>();
        // used to set the currently active account note + driver notes being viewed/edited
        const [accountNoteModalInputs, setAccountNoteModalInputs] = useState<AccountNoteModalFetchInputs | null>(null);
        const [activeAccountName, setActiveAccountName] = useState<string>("");

        const config = useConfig();

        useEffect(
            () => {
                if (!tableData) {
                    return;
                }
                if (tableData.length > 0) {
                    const newRows: IRowData[] = [];
                    const newNoteCurrentValueByAccountIdAndVersionIdRecord:Record<string, (string|null)> = {};
                    for (const sad of tableData) {
                        const currentRowPartialData: Omit<IRowData, "varAmount" | "varPercentage" | "varPercentageClassName" | "isIncome"> = {
                            accountId: sad.accountId,
                            glNumber: sad.accountGlNumber,
                            glName: sad.accountGlName,
                            reforecastVersionId: sad.reforecastVersionId,
                            budgetVersionId: sad.budgetVersionId,
                            isStarred: sad.isBudgetVersionStarred,
                            reforecastValue: parseFloat(sad.reforecastValue ? sad.reforecastValue.toString() : ""),
                            budgetValue: parseFloat(sad.budgetValue ? sad.budgetValue.toString() : ""),
                            reforecastNotes: sad.reforecastVersionNote ?? null,
                            budgetNotes: sad.budgetVersionNote ?? null,
                        };

                        let currentVarAmount = 0;
                        let currentVarPercentage = 0;
                        if (currentRowPartialData) {
                            currentVarAmount = varianceAmount(
                                sad.budgetValue ? currentRowPartialData.budgetValue : null,
                                sad.reforecastValue ? currentRowPartialData.reforecastValue : null) ?? NaN;
                            currentVarPercentage = calcVarianceNullSafe(
                                sad.reforecastValue ? currentRowPartialData.reforecastValue : 0,
                                sad.budgetValue ? currentRowPartialData.budgetValue : 0) ?? NaN;
                        }

                        const parentCategories = config.chartOfAccountsConfig.getParentCategories(sad.parentAccountId ?? "");
                        const currentIsIncome = parentCategories.some(agg => agg.budgetComponentType === "INCOME");

                        if (!currentIsIncome && currentVarAmount !== 0) {
                            currentVarAmount = currentVarAmount * -1;
                        }
                        let currentVarPercentageClassName = styles.green;
                        if ((currentIsIncome && currentVarPercentage < 0) || (!currentIsIncome && currentVarPercentage > 0)) {
                            currentVarPercentageClassName = styles.red;
                        }
                        const currentRowData: IRowData = {
                            ...currentRowPartialData,
                            varAmount: currentVarAmount,
                            varPercentage: currentVarPercentage,
                            isIncome: currentIsIncome,
                            varPercentageClassName: currentVarPercentageClassName
                        };
                        newRows.push(currentRowData);
                        newNoteCurrentValueByAccountIdAndVersionIdRecord[`${sad.accountId}#${sad.reforecastVersionId}`] = sad.reforecastVersionNote ?? null;
                        newNoteCurrentValueByAccountIdAndVersionIdRecord[`${sad.accountId}#${sad.budgetVersionId}`] = sad.budgetVersionNote ?? null;
                    }
                    setData(newRows);
                } else {
                    setData([]);
                }
            }, [tableData]
        );

        const sortClickHandler = (fieldName: SortableFields) => {
            if (sortByField === fieldName) {
                if (sortDirection === "asc") {
                    setSortDirection("desc");
                } else if (sortDirection === "desc") {
                    setSortDirection(undefined);
                } else {
                    setSortDirection("asc");
                }
            } else {
                setSortByField(fieldName);
                setSortDirection("asc");
            }
        };

        if (data === undefined || !property) {
            return <div className={styles.shimmerContainer}>
                <img width={"100%"} src={AccountTableViewShimmer} alt=""/>
            </div>;
        }

        const reforecastYearShortForm = `'${property.reforecastYear % 100}`;
        const budgetYearShortForm = `'${propertyDisplayBudgetYear(property) % 100}`;

        return (
            <div style={style} className={styles.tableContainer}>
                <div className={styles.caption}>
                    {headerText} ({data.length})
                </div>
                <Table>
                    <Head>
                        <HeaderRow className={styles.headerRow}>
                            <SortableCell
                                width={110}
                                onClick={() => {
                                    sortClickHandler("glNumber");
                                }}
                                sort={sortByField === "glNumber" ? sortDirection : undefined}
                            >
                                GL #
                            </SortableCell>
                            <SortableCell
                                width={220}
                                onClick={() => {
                                    sortClickHandler("glName");
                                }}
                                sort={sortByField === "glName" ? sortDirection : undefined}
                            >
                                GL Account Name
                            </SortableCell>
                            {!property.isAcquisitionProperty &&
                                <HeaderCell width={110}>
                                    {reforecastYearShortForm} Reforecast
                                </HeaderCell>
                            }
                            <HeaderCell width={110}>
                                {budgetYearShortForm} Budget
                            </HeaderCell>
                            {!property.isAcquisitionProperty &&
                                <>
                                    <SortableCell
                                        width={110}
                                        onClick={() => {
                                            sortClickHandler("varPercentage");
                                        }}
                                        sort={sortByField === "varPercentage" ? sortDirection : undefined}
                                    >
                                        Var %
                                    </SortableCell>
                                    <SortableCell
                                        width={110}
                                        onClick={() => {
                                            sortClickHandler("varAmount");
                                        }}
                                        sort={sortByField === "varAmount" ? sortDirection : undefined}
                                    >
                                        Var $
                                    </SortableCell>
                                    <HeaderCell>{reforecastYearShortForm} Reforecast Notes</HeaderCell>
                                </>
                            }
                            <HeaderCell>{budgetYearShortForm} Budget Notes</HeaderCell>
                        </HeaderRow>
                    </Head>
                </Table>
                <div className={styles.tableRowsContainer}>
                    <Table>
                        <Body>
                            {sortData(data.slice(), sortByField, sortDirection).map(row => (
                                <Row key={row.accountId}>
                                    <Cell
                                        width={110}
                                    >
                                        <Anchor isExternal={false}
                                                className={styles.glDetailsText}
                                                href={`/account/${row.accountId}/${property.isAcquisitionProperty ? "reforecast": "budget"}`}
                                                target="_blank">
                                            {row.glNumber}
                                        </Anchor>
                                    </Cell>
                                    <Cell
                                        width={220}
                                    >
                                        <div className={styles.glNameContainer}>
                                            <Anchor isExternal={false}
                                                    className={styles.glDetailsText}
                                                    href={`/account/${row.accountId}/${property.isAcquisitionProperty ? "reforecast": "budget"}`}
                                                    target="_blank">
                                                {row.glName}
                                            </Anchor>
                                            <StarredAccountForAccountsTableView
                                                accountId={row.accountId}
                                                reforecastVersionId={row.reforecastVersionId}
                                                budgetVersionId={row.budgetVersionId}
                                                isStarred={row.isStarred}
                                                onUpdate={() => {
                                                    if (typeof onStarringUpdate === "function") {
                                                        onStarringUpdate();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </Cell>
                                    <Cell width={110} className={styles.reforecastBudgetValue}>
                                        {varianceValToString(isNaN(row.reforecastValue) ? null : row.reforecastValue, VarianceType.VARIANCE_AMOUNT, true)}
                                    </Cell>

                                    {!property.isAcquisitionProperty &&
                                        <>
                                            <Cell width={110} className={styles.reforecastBudgetValue}>
                                                {varianceValToString(isNaN(row.budgetValue) ? null : row.budgetValue, VarianceType.VARIANCE_AMOUNT, true)}
                                            </Cell>
                                            <Cell width={110}>
                                                <div
                                                    className={`${styles.varPercentageValContainer} ${row.varPercentageClassName}`}>
                                                    {
                                                        isNaN(row.varPercentage)
                                                                ? '∞%'
                                                                : varianceValToString(row.varPercentage, VarianceType.VARIANCE_PERCENT, false)
                                                    }
                                                </div>
                                            </Cell>
                                            <Cell width={110} className={styles.varValue}>
                                                {varianceValToString(isNaN(row.varAmount) ? null : row.varAmount, VarianceType.VARIANCE_AMOUNT, true)}
                                            </Cell>
                                        </>
                                    }
                                    <Cell>
                                        <AccountNoteViewButton
                                            onActivate={() => setAccountNoteModalInputs({
                                                propertyId: property.id,
                                                accountId: row.accountId,
                                                budgetYear: property.budgetYear,
                                                versionType: VersionType.Reforecast,
                                            })}
                                            notePreview={row.reforecastNotes}
                                        />
                                    </Cell>
                                    {!property.isAcquisitionProperty &&
                                        <Cell>
                                            <AccountNoteViewButton
                                                onActivate={() => {
                                                    setAccountNoteModalInputs({
                                                        propertyId: property.id,
                                                        accountId: row.accountId,
                                                        budgetYear: property.budgetYear,
                                                        versionType: VersionType.Budget,
                                                    });
                                                    setActiveAccountName(row.glName);
                                                }}
                                                notePreview={row.budgetNotes}
                                            />
                                        </Cell>
                                    }
                                </Row>
                            ))}
                        </Body>
                    </Table>
                </div>
                <AccountNoteModal
                    accountName={activeAccountName}
                    noteFetchInputs={accountNoteModalInputs}
                    onSave={() => {
                        setAccountNoteModalInputs(null);
                        onNoteUpdate?.();
                    }}
                    onClose={() => setAccountNoteModalInputs(null)}
                />
            </div>
        );
    };
